import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Pagination,
  Avatar,
} from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ExploreIcon from "@mui/icons-material/Explore";
import { getPublicBrackets, type PublicBracket } from "../api/getCommands";
import { likeBracket, unlikeBracket } from "../api/postCommands";
import SeoHead from "../components/SeoHead";
import AdBanner from "../components/AdBanner";
import { formatDistanceToNow } from "date-fns";
import { useTranslation } from "react-i18next";

const gameModeLabels: Record<string, string> = {
  ArtistBrackets: "Artist",
  customBrackets: "Custom",
  ArtistvsArtist: "Vs",
};

export default function ExplorePage() {
  const [brackets, setBrackets] = useState<PublicBracket[]>([]);
  const [sort, setSort] = useState<"recent" | "popular">("recent");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    setLoading(true);
    getPublicBrackets(page, sort)
      .then((data) => {
        setBrackets(data.brackets);
        setTotalPages(data.totalPages);
      })
      .catch(() => setBrackets([]))
      .finally(() => setLoading(false));
  }, [page, sort]);

  async function handleLike(bracket: PublicBracket) {
    const prev = [...brackets];
    // Optimistic update
    setBrackets((bs) =>
      bs.map((b) =>
        b.Id === bracket.Id
          ? {
              ...b,
              user_liked: !b.user_liked,
              likes_count: b.user_liked ? b.likes_count - 1 : b.likes_count + 1,
            }
          : b,
      ),
    );

    try {
      const result = bracket.user_liked
        ? await unlikeBracket(bracket.Id)
        : await likeBracket(bracket.Id);

      if (result.success) {
        setBrackets((bs) =>
          bs.map((b) =>
            b.Id === bracket.Id
              ? { ...b, likes_count: result.likes_count }
              : b,
          ),
        );
      } else {
        setBrackets(prev);
      }
    } catch {
      setBrackets(prev);
    }
  }

  function formatDate(dateStr: string) {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch {
      return dateStr;
    }
  }

  return (
    <Box id="explore-page" sx={{ maxWidth: 800, mx: "auto", mt: 4 }}>
      <SeoHead
        title="Explore"
        description="Browse and like brackets created by other Music Brackets players."
        canonical="/explore"
      />
      <Typography id="explore-title" variant="h4" gutterBottom>
        <ExploreIcon sx={{ mr: 1, verticalAlign: "middle" }} />
        {t("explore.title")}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Discover brackets created by the Music Brackets community. See which songs other players
        crowned as their favorites, browse recent and popular results, and like the brackets you
        enjoy.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Every bracket on this page was completed by a real player who chose to share their results.
        Use the tabs below to sort by most recent or most popular. Sign in with Google to save your
        own completed brackets and share them here for the community to see.
      </Typography>

      <Tabs
        id="explore-tabs"
        value={sort}
        onChange={(_, val) => {
          setSort(val);
          setPage(1);
        }}
        sx={{ mb: 3 }}
      >
        <Tab label={t("explore.recent")} value="recent" />
        <Tab label={t("explore.popular")} value="popular" />
      </Tabs>

      {loading && brackets.length === 0 && (
        <Typography id="explore-loading" color="text.secondary" textAlign="center" sx={{ mt: 4 }}>
          {t("explore.loading")}
        </Typography>
      )}

      {!loading && brackets.length === 0 && (
        <Card id="explore-empty" sx={{ mt: 3 }}>
          <CardContent sx={{ textAlign: "center", py: 6 }}>
            <Typography variant="h6" color="text.secondary">
              {t("explore.noBrackets")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("explore.noBracketsDesc")}
            </Typography>
          </CardContent>
        </Card>
      )}

      {brackets.map((bracket) => (
        <Card key={bracket.Id} sx={{ mb: 2 }}>
          <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              src={bracket.profile_picture}
              sx={{ width: 40, height: 40 }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ lineHeight: 1.3 }}>
                {bracket.winner_song_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {bracket.winner_artist}
                {bracket.artist_selected && ` — ${bracket.artist_selected}`}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mt: 0.5, alignItems: "center" }}>
                <Typography variant="caption" color="text.secondary">
                  {t("explore.by")} {bracket.display_name}
                </Typography>
                <Chip
                  label={`${bracket.bracket_size} ${gameModeLabels[bracket.game_mode] || bracket.game_mode}`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={bracket.game_type === "DoubleE" ? "Double" : "Single"}
                  size="small"
                  variant="outlined"
                />
                <Typography variant="caption" color="text.secondary">
                  {formatDate(bracket.created_date)}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <IconButton
                onClick={() => handleLike(bracket)}
                color={bracket.user_liked ? "error" : "default"}
                size="small"
              >
                {bracket.user_liked ? <FavoriteIcon /> : <FavoriteBorderIcon />}
              </IconButton>
              <Typography variant="body2" color="text.secondary">
                {bracket.likes_count}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ))}

      {totalPages > 1 && (
        <Box id="explore-pagination" sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, val) => setPage(val)}
          />
        </Box>
      )}

      {!loading && brackets.length > 0 && <AdBanner adSlot="1234567892" />}

      {/* Explore page info */}
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          About the Explore Page
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          The Explore page is a community-driven feed of completed music brackets. When players
          finish a bracket tournament and toggle it as public, their results appear here for
          everyone to see. Each card shows the winning song, the artist, the bracket format, and
          when it was played.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Browsing other people's brackets is a great way to discover new music. You might find an
          artist you've never heard of, or see a deep cut crowned over a popular hit and wonder
          what makes it special. Like brackets that catch your eye — the most-liked results rise to
          the top of the Popular tab.
        </Typography>
      </Box>
    </Box>
  );
}
